import { useSearchParams } from "react-router-dom";
import {
  AggregateScoreType,
  useGetAggregateScoresQuery,
  useGetPolicyQuery,
  useLoadMqueryQuery,
} from "~/operations";
import { PLATFORM_POLICY_MRN, PolicyIdToMrn } from "~/lib/mrn";
import { VariantIcon } from "~/components/ui-library";

export function usePlatformCheck(policyId?: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const findingMrn = searchParams.get("findingMrn") || "";

  const {
    data: checkData,
    loading: checkLoading,
    error: checkError,
  } = useLoadMqueryQuery({
    variables: {
      input: {
        mrn: findingMrn,
      },
    },
    skip: !findingMrn,
  });

  const {
    data: aggData,
    loading: aggLoading,
    error: aggError,
  } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: PLATFORM_POLICY_MRN,
      filter: {
        findingMrn: findingMrn,
        scoreType: AggregateScoreType.Check,
      },
      skipCases: true,
    },
    skip: !findingMrn,
  });

  const { data: policyData, loading: policyLoading } = useGetPolicyQuery({
    variables: { input: { mrn: PolicyIdToMrn(policyId || "") } },
    skip: !policyId,
  });

  const check = checkData?.mquery;
  const aggScores = aggData?.aggregateScores;
  const aggScoreEdges =
    aggScores?.__typename === "AggregateScoresConnection"
      ? aggScores.edges
      : [];
  const checkScore = (
    aggScores?.__typename === "AggregateScoresConnection"
      ? aggScoreEdges?.flatMap((edge) => edge?.node ?? [])
      : []
  )?.pop();

  const blastRadius = checkScore?.blastRadius;
  const loading = checkLoading || aggLoading || policyLoading;
  const hasError = checkError || aggError;

  // Extract policies from aggregate score tags
  const policiesTag = checkScore?.tags?.find((tag) => tag?.key === "policies");
  const policies = policiesTag?.value
    ? policiesTag.value.split(",").map((p) => p.trim())
    : [];

  const variants = [
    ...(
      check?.variants?.map((variant) => ({
        label: variant?.title || "",
        value: variant?.id || "",
        icon: <VariantIcon type={variant?.icon || ""} />,
      })) || []
    ).sort((a, b) => (a.label > b.label ? 1 : -1)),
  ];
  const selectedVariantId = searchParams.get("variantQuery");
  const selectedVariant =
    variants.find((v) => v.value === selectedVariantId) || variants[0];
  const hasVariants = check?.variants && check.variants.length > 0;

  return {
    isLoading: loading,
    hasError: !!hasError,
    check,
    checkScore,
    aggScores,
    blastRadius,
    title: check?.title || "",
    findingMrn,
    policy: policyData?.policy,
    lastScannedAt: checkScore?.lastScannedAt,
    firstDetectedAt: checkScore?.firstDetectedAt,
    policies,
    variants,
    selectedVariant,
    hasVariants,
    searchParams,
    setSearchParams,
  };
}
